import { BusinessCenter as BusinessCenterIcon } from 'components/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { MatterAmountFormTable } from 'pages/matters/tables/MatterAmountFormTable';
import { MATTER_SEARCH_BAR_SELECTORS } from 'pages/matters/tables/MattersAccordionTable';
import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useGetMattersStatesQuery } from 'store/api/matters';
import { t } from 'utils/getTranslations';
import { buildRsqlFromSearchText } from 'utils/searchQueryToRsql';
import { useClassifiers } from 'utils/useClassifiers';
import { rejectedSubmitHandler } from 'utils/useRejectedSubmitCapture';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

export interface MatterAmountForm {
    id: number;
    officeId: number;
    payerId: number;
    currency: string;
    selected: boolean;
    amount: string;
}

export interface AddNewCreditInvoiceForm {
    client: { id: number; name: string } | null;
    inProgress: MatterAmountForm[];
    pending: MatterAmountForm[];
    completed: MatterAmountForm[];
    offerWon: MatterAmountForm[];
    offerFailed: MatterAmountForm[];
    archived: MatterAmountForm[];
}

export type InputScopeType = {
    currency: string;
    payerId: number;
    officeId: number;
};

interface Props {
    onSubmit: (formData: AddNewCreditInvoiceForm) => void;
    onClose: () => void;
}

const defaultValues: AddNewCreditInvoiceForm = {
    client: null,
    inProgress: [],
    pending: [],
    completed: [],
    offerWon: [],
    offerFailed: [],
    archived: [],
};

const USED_CLASSIFIERS = ['CURRENCY'];

const AddNewPrepaidInvoiceFormModal = (props: Props) => {
    const { onClose, onSubmit } = props;
    const [searchBarState, setSearchBarState] = useState('');
    const [searchText, setSearchText] = useState('');
    const [searchFilter, setSearchFilter] = useState<string>();
    const { classifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const [selectedInputScopes, setSelectedInputScopes] = useState<InputScopeType[]>([]);
    const { handleSubmit, control, setValue, getValues } = useForm<AddNewCreditInvoiceForm>({
        defaultValues: defaultValues,
        values: defaultValues,
    });

    const [client, inProgress, pending, completed, offerWon, offerFailed] = useWatch({
        control,
        name: ['client', 'inProgress', 'pending', 'completed', 'offerWon', 'offerFailed'],
    });

    const hasScopeMismatch = selectedInputScopes.length > 1;
    const hasMissingAmount = useMemo(() => {
        const allMatters = [
            ...(inProgress ?? []),
            ...(pending ?? []),
            ...(completed ?? []),
            ...(offerWon ?? []),
            ...(offerFailed ?? []),
        ];
        const selectedMatters = allMatters.filter((matter) => matter?.selected);
        return !selectedMatters.length || selectedMatters.some((matter) => !matter.amount);
    }, [inProgress, pending, completed, offerWon, offerFailed]);

    const isSaveDisabled = hasScopeMismatch || hasMissingAmount;

    const disabledSaveInfoTextId = hasScopeMismatch ? 'invoices.new-credit-note-modal.disabled-save-info' : undefined;

    const { data: matterStateCount } = useGetMattersStatesQuery(
        client
            ? {
                  clientIds: [client.id],
                  filter: searchFilter,
              }
            : skipToken
    );

    const onSave = async () => {
        await handleSubmit((data) => onSubmit(data), rejectedSubmitHandler('invoice-credit-note'))();
        onClose();
    };

    const resetSelections = useCallback(() => {
        setValue('offerWon', []);
        setValue('offerFailed', []);
        setValue('pending', []);
        setValue('completed', []);
        setValue('inProgress', []);
        setValue('archived', []);
        setSelectedInputScopes([]);
    }, [setValue]);

    const resetAll = useCallback(() => {
        resetSelections();
        setSearchBarState('');
        setSearchText('');
        setSearchFilter(undefined);
    }, [resetSelections]);

    const handleSearchBarChange = useCallback((value = '') => {
        setSearchBarState(value);
    }, []);

    const handleSearch = useCallback(
        (searchText: string) => {
            resetSelections();
            setSearchText(searchText);
            // Built here, not in render: building the search warns when keywords were dropped.
            setSearchFilter(buildRsqlFromSearchText(searchText, MATTER_SEARCH_BAR_SELECTORS) || undefined);
        },
        [resetSelections]
    );

    const handleSearchDebounced = useDebouncedCallback((value = '') => {
        handleSearch(value);
    }, 600);

    return (
        <>
            <ModalHeader title={t('invoices.new-credit-note-modal.title')} onCancel={onClose} />
            <div className="bg-grey-50 h-[800px] overflow-y-auto p-6 max-sm:px-4">
                <div className="flex flex-col gap-4">
                    <ContactDynamicSelectPicker
                        setValue={setValue}
                        control={control}
                        label="invoices.new-prepayment-modal.client"
                        required
                        name="client"
                        paginated={{
                            fetch: useLazyGetMinContactsListWithFiltersQuery,
                            searchField: ['name', 'partyNumber'],
                        }}
                        PickerModalComponent={ContactPickerModal}
                        placeholder="invoices.new-prepayment-modal.client"
                        onChange={resetAll}
                    />
                    {client ? (
                        <>
                            <h5 className="text-h5 m-0 pt-2">{t('invoices.new-credit-note-modal.client-cases')}</h5>
                            <div className="my-6">
                                <SearchBar
                                    value={searchBarState}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        handleSearchBarChange(value);
                                        handleSearchDebounced(value);
                                    }}
                                    pluralTextId="matters.matters"
                                    singularTextId="matters.matter"
                                    placeHolder={t('matters.search-cases')}
                                />
                            </div>
                            <MatterAmountFormTable
                                control={control}
                                setValue={setValue}
                                getValues={getValues}
                                selectedInputScopes={selectedInputScopes}
                                setSelectedInputScopes={setSelectedInputScopes}
                                classifiers={classifiers}
                                matterStateCount={matterStateCount}
                                passiveFilter={`clientParty.id=in=(${client.id})`}
                                searchText={searchText}
                                disableUrlState
                            />
                        </>
                    ) : (
                        <div className="opacity-60">
                            <div className="mt-6 flex justify-center">
                                <BusinessCenterIcon className="text-primary" />
                            </div>
                            <div className="mt-1.5 flex justify-center">
                                {t('invoices.new-credit-note-modal.select-client')}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ModalFooter
                disabledSaveInfoTextId={disabledSaveInfoTextId}
                disableSave={isSaveDisabled}
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
            />
        </>
    );
};

export default AddNewPrepaidInvoiceFormModal;
